import React, { useState, useEffect, useRef, useContext} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import './FilePreviewModal.css';
import mammoth from 'mammoth/mammoth.browser.js';
import expand_icon from '../../assets/icons/expand_icon.svg';
import collapse_icon from '../../assets/icons/collapse_icon.svg';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { AppContext } from '../../context/appContext.js';
import SvgIcon from '../common/SvgIcon.js';
import CloseIcon from '../../assets/icons/close_icon2.svg';
import useIsMobile from '../../hooks/useIsMobile.js';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.mjs`;

const FilePreviewModal = ({ 
            file, 
            onClose, 
            embedded = false, 
            matchedKeywords,
            editedName,
            pageNumber=1,
            numPages=null,
            onPageChange,
            onDocumentLoad,
            onReset
            }) => {

  const [content, setContent] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
 
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(500);
  const {isPopUp, setIsPopUp, isExpanded, setIsExpanded} =useContext(AppContext);
  const togglePopUp = () => setIsPopUp(prev => !prev);
  const toggleExpand = () => setIsExpanded(prev => !prev);
  const escapeHtml = str =>
  str.replace(/&/g, '&amp;')
     .replace(/</g, '&lt;')
     .replace(/>/g, '&gt;');

  const escapeRegExp = str =>
  str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  const tokenRegex = /(\w+|\W+)/g;
  const isMobile = useIsMobile(768);

  const getValidKeywords = () =>
  matchedKeywords
    .map(k => k.trim())
    .filter(k => k.length > 0)
    .map(k => k.toLowerCase());

  // Enable highlights only for embedded preview (results card)
  const enableHighlights = embedded && Array.isArray(matchedKeywords) && matchedKeywords.length > 0;

  const getHighlightedText = text => {
    if (!enableHighlights) return escapeHtml(text || '');
    return highlightString(text || '');
  };

  useEffect(() => {}, [matchedKeywords]);

  useEffect(() => {
    if (containerRef.current) {
      const updateWidth = () => {
        // Use precise fractional width; clientWidth rounds to an integer
        const rect = containerRef.current.getBoundingClientRect();
        setContainerWidth(rect.width);
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // With customTextRenderer we don't need to manually draw overlays
  }, [pageNumber, matchedKeywords, numPages]);
  
  useEffect(() => {
      if (!file) {
        setPdfUrl('');
        setContent('');
        setIsLoading(false);
        setError(null);
        if (onReset) onReset();
        return;
      }
    setIsLoading(true);
    setError(null);

    const processFile = async () => {
      try {
        if (file.type === 'application/pdf') {
          const url = URL.createObjectURL(file);
          setPdfUrl(url);
        } else if (file.type === 'text/plain') {
          setContent(await readFileAsText(file));
        } else if (
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) {
          setContent(await readDocxFile(file));
        }
      } catch (err) {
        setError(`Failed to load file: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    processFile();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [file]);

  const readFileAsText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });

  const readDocxFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const result = await mammoth.extractRawText({ arrayBuffer: e.target.result });
        resolve(result.value);
      } catch (err) {
        reject(new Error('Failed to extract DOCX content'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read DOCX file'));
    reader.readAsArrayBuffer(file);
  });

  function highlightString(raw) {
    const valid = getValidKeywords();
    if (valid.length === 0) return escapeHtml(raw);
    const parts = raw.split(/(\W+)/);
    return parts.map(part => {
      for (let kw of valid) {
        const safe = escapeRegExp(kw);
        const re = kw.length === 1
          ? new RegExp(`^${safe}$`)
          : new RegExp(`^${safe}$`, 'i');
        if (re.test(part)) {
          return `<span class="pdf-keyword-match">${escapeHtml(part)}</span>`;
        }
      }
      return escapeHtml(part);
    }).join('');
  }

  function buildKeywordRegex(keywords) {
    const normalized = (keywords || [])
      .map(k => (k || '').trim())
      .filter(k => k.length > 0);
    if (normalized.length === 0) return null;
    const patterns = normalized.map(k => {
      const parts = k.split(/[\s\-_]+/).filter(Boolean).map(p => escapeRegExp(p));
      if (parts.length === 0) return null;
      return `\\b${parts.join('(?:[\\s\\-_]+)')}\\b`;
    }).filter(Boolean);
    if (patterns.length === 0) return null;
    return new RegExp(`(${patterns.join('|')})`, 'gi');
  }

  // Build keyword regex and inline highlighter that returns an HTML string for PDF text layer
  const keywordRegex = React.useMemo(() => buildKeywordRegex(enableHighlights ? matchedKeywords : []), [matchedKeywords, enableHighlights]);

  function renderPdfTextWithMarks(str) {
    const text = str || '';
    if (!keywordRegex) return escapeHtml(text);
    let out = '';
    let last = 0;
    keywordRegex.lastIndex = 0;
    let m;
    while ((m = keywordRegex.exec(text)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      if (start > last) out += escapeHtml(text.slice(last, start));
      out += `<mark class="pdf-keyword-mark">${escapeHtml(m[0])}</mark>`;
      last = end;
    }
    if (last < text.length) out += escapeHtml(text.slice(last));
    return out;
  }

  const customTextRenderer = React.useCallback(({ str }) => {
    return renderPdfTextWithMarks(str);
  }, [keywordRegex]);

  // Fallback overlay using Range rects for precision on complex PDFs
  const drawOverlayHighlights = React.useCallback(() => {
    // Only draw overlay when expanded; collapsed view relies on inline marks
    if (!isExpanded || !enableHighlights || !pageNumber || !matchedKeywords || matchedKeywords.length === 0) return;
    const pageEl = document.querySelector(`.react-pdf__Page[data-page-number="${pageNumber}"]`);
    if (!pageEl) return;
    const textLayer = pageEl.querySelector('.react-pdf__Page__textContent');
    if (!textLayer) return;
    let overlay = textLayer.querySelector(':scope > .pdf-overlay-rects');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'pdf-overlay-rects';
      overlay.style.position = 'absolute';
      overlay.style.inset = '0';
      overlay.style.pointerEvents = 'none';
      textLayer.appendChild(overlay);
    }
    overlay.innerHTML = '';
    const walker = document.createTreeWalker(textLayer, NodeFilter.SHOW_TEXT, null);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    const regex = buildKeywordRegex(matchedKeywords);
    if (!regex) return;
    // account for parent transform scale to prevent double-scaling
    const transform = getComputedStyle(textLayer).transform;
    let scaleX = 1, scaleY = 1;
    if (transform && transform !== 'none') {
      const m2d = transform.match(/matrix\(([^)]+)\)/);
      if (m2d) {
        const parts = m2d[1].split(',').map(v => parseFloat(v.trim()));
        if (parts.length >= 4) { scaleX = parts[0]; scaleY = parts[3]; }
      } else {
        const m3d = transform.match(/matrix3d\(([^)]+)\)/);
        if (m3d) {
          const parts = m3d[1].split(',').map(v => parseFloat(v.trim()));
          if (parts.length >= 16) { scaleX = parts[0]; scaleY = parts[5]; }
        }
      }
    }
    const layerBox = textLayer.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const snap = v => Math.round(v * dpr) / dpr;
    nodes.forEach(n => {
      const val = n.nodeValue || '';
      regex.lastIndex = 0;
      let m;
      while ((m = regex.exec(val)) !== null) {
        const r = document.createRange();
        r.setStart(n, m.index);
        r.setEnd(n, m.index + m[0].length);
        const rects = r.getClientRects();
        for (let i = 0; i < rects.length; i++) {
          const rc = rects[i];
          const box = document.createElement('div');
          box.className = 'pdf-keyword-rect';
          box.style.position = 'absolute';
          const top = (rc.top - layerBox.top) / (scaleY || 1);
          const left = (rc.left - layerBox.left) / (scaleX || 1);
          const width = rc.width / (scaleX || 1);
          const height = rc.height / (scaleY || 1);
          box.style.top = `${snap(top)}px`;
          box.style.left = `${snap(left)}px`;
          box.style.width = `${snap(width)}px`;
          box.style.height = `${snap(height)}px`;
          overlay.appendChild(box);
        }
        r.detach();
      }
    });
  }, [pageNumber, matchedKeywords, enableHighlights, isExpanded]);

  useEffect(() => {
    // re-draw on container width or expand changes
    drawOverlayHighlights();
  }, [containerWidth, isExpanded, pageNumber, matchedKeywords, drawOverlayHighlights]);
  
  // Ensure any existing overlay is removed when collapsing the preview
  const clearOverlayHighlights = React.useCallback(() => {
    const pageEl = document.querySelector(`.react-pdf__Page[data-page-number="${pageNumber}"]`);
    if (!pageEl) return;
    const textLayer = pageEl.querySelector('.react-pdf__Page__textContent');
    if (!textLayer) return;
    const overlay = textLayer.querySelector(':scope > .pdf-overlay-rects');
    if (overlay) overlay.remove();
  }, [pageNumber]);

  useEffect(() => {
    if (!isExpanded) {
      clearOverlayHighlights();
    }
  }, [isExpanded, clearOverlayHighlights]);
  
  const onDocumentLoadSuccess = ({ numPages }) => { if (onDocumentLoad) onDocumentLoad({ numPages }); };

  const renderPdfPreview = () => (
    <>
      <div   className={`pdf-preview ${embedded ? 'embedded':''} ${isExpanded ? 'expanded' : ''}`}
            onClick={(e)=> { e.stopPropagation(); toggleExpand(); }}
      >
       <div className={`preview-wrapper ${embedded ? 'embedded':''} ${isExpanded ? 'expanded' : ''}`} ref={containerRef}>
        <Document 
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={() => setError('Failed to render PDF. Please try another file.')}
          loading={<div className='pdf-loading'>Rendering PDF...</div>}
        >
          <Page
            pageNumber={pageNumber || 1}
            width={Math.min(containerWidth, 1000)}
            renderTextLayer={true}
            renderAnnotationLayer={false}
            customTextRenderer={enableHighlights ? customTextRenderer : undefined}
            className={isExpanded ? 'pdf-page-expanded' : ''}
            loading={<div className='pdf-loading'>Loading page...</div>}
            renderMode='canvas'
            optimizePDF='true'
            onRenderSuccess={() => {
              if (enableHighlights && isExpanded) {
                // double rAF to ensure transforms are applied before measuring
                requestAnimationFrame(() => requestAnimationFrame(drawOverlayHighlights));
              } else if (!isExpanded) {
                clearOverlayHighlights();
              }
            }}
          />
        </Document>
       </div>
       {isExpanded && embedded && numPages > 0 && (
          <div className='pdf-controls-grid'
                onClick={e => e.stopPropagation()}>
            <button
                 onClick={e => { e.stopPropagation(); onPageChange(p => Math.max(1, p - 1)); }}
                 disabled={pageNumber <= 1}
            >
              ← Previous
            </button>
            <span>Page {pageNumber} of {numPages}</span>
            <button onClick={e => { e.stopPropagation(); onPageChange(p => Math.min(numPages, p + 1)); }} disabled={pageNumber >= numPages}>
              Next →
            </button>
          </div>
        )}
      </div>
    </>
  );

  const renderTextPreview = () => (
    <div className={`text-preview ${!isExpanded ? 'Collapse' : 'Expand'}`}>
      <pre
        dangerouslySetInnerHTML={{ __html: getHighlightedText(content) }}
      />
    </div>
  );

  const renderContent = () => {
    if (!file) return <div className='preview-error'>No file selected</div>;
    if (error) return <div className='preview-error'>{error}</div>;
    if (isLoading) return <div className='preview-loading'>Loading file...</div>;
    switch (file.type) {
      case 'application/pdf':
        return renderPdfPreview();
      case 'text/plain':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return renderTextPreview();
      default:
        return <div className='preview-error'>Unsupported file type</div>;
    }
  };

  if (embedded) {
    return (
      <>
        {isExpanded && (<div className='pdf-preview-embedded'></div>)}
        {renderContent()}
      </>
    );
  }

  return (
    <div className='preview-modal-overlay' onClick={() => {onClose(); togglePopUp(); if(isExpanded) {toggleExpand()}}}>
      <div className={`preview-modal-content ${embedded ? '':'grid'} ${isExpanded ? 'Expand':''}`} 
           onClick={(e) => { e.stopPropagation(); if (e.target.closest('.preview-modal-content-top')) return; toggleExpand();}}>
        <div className={`preview-modal-content-top ${isExpanded ? 'Expand':''}`}>
          <button className='preview-close-button' 
            onClick={() => {onClose();togglePopUp(); if(isExpanded) {toggleExpand();}}}> 
              <SvgIcon src={CloseIcon} color='#ddd' hoverColor='#6e41c1' size='28px' className={`preview-close-icon ${isExpanded ? 'Expand':''}`} />
          </button>
          <h3>{editedName || 'File Preview'}</h3>
          <button 
            className='expand-toggle-button'
            onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <img src={isExpanded ? collapse_icon : expand_icon} alt={isExpanded ? 'Collapse' : 'Expand'} />
          </button>
        </div>
        {renderContent()}
         {numPages > 0 && (
          <div className='pdf-controls-grid' onClick={e => e.stopPropagation()}>
            <button onClick={e => { e.stopPropagation(); onPageChange(p => Math.max(1, p - 1)); }} disabled={pageNumber <= 1}>← Previous</button>
            <span>Page {pageNumber} of {numPages}</span>
            <button onClick={e => { e.stopPropagation(); onPageChange(p => Math.min(numPages, p + 1)); }} disabled={pageNumber >= numPages}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilePreviewModal;


